import type { AppointmentTypeInfo, Patient } from '@store/patientStore';
import type { AppTypeDTO } from '@utils/interfaces';

export interface AppointmentQuestionAnswers {
  clBefore: string | undefined;
  eyeExamWithUs: boolean | undefined;
}

export const getAppointmentTypeFromUrl = ({
  appTypeIdFromUrl,
  appointmentTypes,
}: {
  appTypeIdFromUrl?: string;
  appointmentTypes?: AppTypeDTO[];
}): AppointmentTypeInfo | undefined => {
  if (!appTypeIdFromUrl || !appointmentTypes) {
    return undefined;
  }

  const name = appointmentTypes.find(
    type => type.id === appTypeIdFromUrl,
  )?.title;

  return name
    ? {
        appointmentType: appTypeIdFromUrl,
        name,
      }
    : undefined;
};

export const buildInitialAppointmentAnswers = (
  patients: Array<Patient | undefined>,
): Record<number, AppointmentQuestionAnswers> =>
  patients.reduce<Record<number, AppointmentQuestionAnswers>>(
    (acc, patient, index) => {
      acc[index] = {
        clBefore: patient?.appointmentType?.clBeforeAnswer,
        eyeExamWithUs: patient?.appointmentType?.eyeExamWithUsAnswer ?? false,
      };

      return acc;
    },
    {},
  );

export const getAppointmentQuestionVisibility = ({
  appointmentType,
  showCLBeforeQuestionFromConfig,
  showEyeExamWithUsQuestionFromConfig,
}: {
  appointmentType?: string;
  showCLBeforeQuestionFromConfig: boolean;
  showEyeExamWithUsQuestionFromConfig: boolean;
}) => ({
  showCLBeforeQuestion: !!(
    appointmentType?.includes('cl') && showCLBeforeQuestionFromConfig
  ),
  showEyeExamWithUsQuestion: showEyeExamWithUsQuestionFromConfig,
});

export const isMultipatientAppointmentFlow = ({
  isStartedNewPatient,
  patients,
}: {
  isStartedNewPatient: boolean;
  patients: Array<Patient | undefined>;
}) => isStartedNewPatient || patients.filter(Boolean).length > 1;

export const getAppointmentPatientDisplayName = ({
  fallbackPatientName,
  patient,
}: {
  fallbackPatientName: string;
  patient?: Patient;
}) => {
  const firstName = patient?.patientDetails?.firstName?.trim();
  const lastName = patient?.patientDetails?.lastName?.trim();

  return firstName || lastName
    ? [firstName, lastName].filter(Boolean).join(' ')
    : fallbackPatientName;
};

export const getIncompleteAppointmentPatientNames = ({
  currentPatientIndex,
  fallbackPatientName,
  patients,
  selectedAppointments,
}: {
  currentPatientIndex: number;
  fallbackPatientName: string;
  patients: Array<Patient | undefined>;
  selectedAppointments: Array<AppointmentTypeInfo | undefined>;
}) =>
  patients.reduce<string[]>((acc, patient, index) => {
    if (
      patient &&
      index !== currentPatientIndex &&
      !selectedAppointments[index]
    ) {
      acc.push(
        getAppointmentPatientDisplayName({ fallbackPatientName, patient }),
      );
    }

    return acc;
  }, []);

export const buildSelectedAppointmentsAfterClick = ({
  currentPatientIndex,
  selectedAppointments,
  type,
}: {
  currentPatientIndex: number;
  selectedAppointments: Array<AppointmentTypeInfo | undefined>;
  type: AppTypeDTO;
}) => {
  const selectedAppointment = {
    appointmentType: type.id,
    name: type.title,
    idTranslation: type.idTranslation,
  };

  return selectedAppointments.length > 0
    ? selectedAppointments.map((appointment, index) =>
        index === currentPatientIndex ? selectedAppointment : appointment,
      )
    : [selectedAppointment];
};

export const isAppointmentTypeStepValid = ({
  answersMap,
  currentPatientIndex,
  patients,
  selectedAppointments,
  showCLBeforeQuestion,
}: {
  answersMap: Record<number, AppointmentQuestionAnswers>;
  currentPatientIndex: number;
  patients: Array<Patient | undefined>;
  selectedAppointments: Array<AppointmentTypeInfo | undefined>;
  showCLBeforeQuestion: boolean;
}) => {
  if (showCLBeforeQuestion && !answersMap[currentPatientIndex]?.clBefore) {
    return false;
  }

  if (!selectedAppointments.some(Boolean)) {
    return false;
  }

  return patients.every(
    (patient, index) => (patient && selectedAppointments[index]) || !patient,
  );
};
